import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, Camera, Settings } from 'lucide-react'
import { Dropdown, DropdownItem } from 'components/ui/Dropdown'
import { Button } from 'components/ui/Button'
import { AvatarSelectionModal } from 'components/ui/AvatarSelectionModal'
import { useAuth } from 'contexts/AuthContext'
import { authApi } from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import { useTranslation } from 'react-i18next'

export function UserDropdown() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, logout, updateUser } = useAuth()
  const { addToast } = useToast()
  const [isUploading, setIsUploading] = useState(false)
  const [showAvatarModal, setShowAvatarModal] = useState(false)

  const handleAvatarClick = () => {
    setShowAvatarModal(true)
  }

  const handleAvatarUpload = async (file: File) => {
    setIsUploading(true)
    try {
      const response = await authApi.uploadAvatar(file)
      updateUser(response.data.user)
      
      addToast({
        type: 'success',
        title: t('header.avatarUpdatedTitle', 'Avatar Updated'),
        message: t('header.avatarUpdatedMessage', 'Your avatar has been updated successfully!'),
        duration: 3000
      })
    } catch (error: any) {
      console.error('Avatar upload failed:', error)
      addToast({
        type: 'error',
        title: 'Upload Failed',
        message: error.response?.data?.error || 'Failed to upload avatar. Please try again.',
        duration: 5000
      })
      throw error // Re-throw so the modal can handle it
    } finally {
      setIsUploading(false)
    }
  }

  const handleDeleteAvatar = async () => {
    try {
      const response = await authApi.deleteAvatar()
      updateUser(response.data.user)
      
      addToast({
        type: 'success',
        title: t('header.avatarDeletedTitle', 'Avatar Deleted'),
        message: t('header.avatarDeletedMessage', 'Your avatar has been deleted successfully!'),
        duration: 3000
      })
    } catch (error: any) {
      console.error('Avatar deletion failed:', error)
      addToast({
        type: 'error',
        title: 'Delete Failed',
        message: error.response?.data?.error || 'Failed to delete avatar. Please try again.',
        duration: 5000
      })
      throw error // Re-throw so the modal can handle it
    }
  }

  const getAvatarUrl = () => {
    if (user?.avatar_url) {
      return user.avatar_url
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.full_name || user?.email || 'User')}&background=random&color=fff&size=128`
  }

  const triggerElement = (
    <button className="relative h-9 w-9 rounded-full overflow-hidden ring-2 ring-border hover:ring-primary transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
      {user?.avatar_url ? (
        <img
          src={user.avatar_url}
          alt={user?.full_name || 'User'}
          className="h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.full_name || user?.email || 'User')}&background=random&color=fff&size=128`
          }}
        />
      ) : (
        <img
          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.full_name || user?.email || 'User')}&background=random&color=fff&size=128`}
          alt={user?.full_name || 'User'}
          className="h-full w-full object-cover"
        />
      )}
    </button>
  )

  return (
    <>
      <Dropdown trigger={triggerElement} className="relative">
        {/* User Info Section */}
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="relative h-14 w-14 rounded-full overflow-hidden flex-shrink-0">
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user?.full_name || 'User'}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.full_name || user?.email || 'User')}&background=random&color=fff&size=128`
                  }}
                />
              ) : (
                <img
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.full_name || user?.email || 'User')}&background=random&color=fff&size=128`}
                  alt={user?.full_name || 'User'}
                  className="h-full w-full object-cover"
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <DropdownItem onClick={handleAvatarClick}>
          <div className="flex items-center space-x-2">
            <Camera className="h-4 w-4" />
            <span>{t('header.changeAvatar')}</span>
          </div>
        </DropdownItem>

        <DropdownItem onClick={() => navigate('/settings')}>
          <div className="flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>{t('header.settings')}</span>
          </div>
        </DropdownItem>

        <div className="border-t border-border">
          <DropdownItem onClick={logout}>
            <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
              <LogOut className="h-4 w-4" />
              <span>{t('header.logout')}</span>
            </div>
          </DropdownItem>
        </div>
      </Dropdown>

      {/* Avatar Selection Modal */}
      <AvatarSelectionModal
        isOpen={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        currentAvatarUrl={user?.avatar_url}
        userName={user?.full_name || user?.email || 'User'}
        onAvatarUpload={handleAvatarUpload}
        onAvatarDelete={handleDeleteAvatar}
        isUploading={isUploading}
      />
    </>
  )
}

